<template>
  <div class="marketplace-checkout">
    <nav class="marketplace-checkout--nav">
      <div class="marketplace-checkout--nav-wrapper container">
        <figure class="marketplace-logo">
          <SloanMarketPlaceLogo />
        </figure>
        <a href="/" class="marketplace-checkout--nav-link">
          <SloIcon name="ArrowLeft" />
          <span class="ml-xs">{{ 'Return to sloan.com' | t }}</span>
        </a>
      </div>
    </nav>
    <section class="container">
      <ul v-if="step !== 6" class="marketplace-checkout--steps">
        <li :class="{active: step === 1}">
          <span>{{ 'Information' | t }}</span>
          <SloIcon name="ChevronRight" />
        </li>
        <li :class="{active: step === 2}">
          <span>{{ 'Address' | t }}</span>
          <SloIcon name="ChevronRight" />
        </li>
        <li :class="{active: step === 3}">
          <span>{{ 'Shipping' | t }}</span>
          <SloIcon name="ChevronRight" />
        </li>
        <li :class="{active: step === 4}">
          <span>{{ 'Payment' | t }}</span>
          <SloIcon name="ChevronRight" />
        </li>
        <li :class="{active: step === 5}">
          <span>{{ 'Review' | t }}</span>
        </li>
      </ul>
    </section>
    <section class="marketplace-checkout--wrapper container">
      <div>
        <ul v-if="step > 1 && step !== 6" class="marketplace-checkout--steps-content-preview">
          <li v-if="step > 1">
            <p class="flex items-start justify-start flex-wrap gap-sm m-0 m-0 c-gray-700">
              <span class="text-base fw-strong c-gray-900">
                <span v-if="authenticated && cartResponse?.user?.name">
                  {{ 'Hello' | t }}, {{ cartResponse?.user?.name }}!
                </span>
                <span v-else-if="authenticated">{{ 'Hello' |t }}!</span>
                <span v-else>{{ 'Contact' | t }}</span>
              </span>
              <span>{{ cartResponse?.user?.uid }}</span>
            </p>
            <button
              v-if="authenticated"
              @click="handleLogout"
              type="button"
              class="bg-transparent p-0 c-blue-400 text-sm fw-strong hover:c-blue-700"
            >
              {{ 'Sign Out' | t }}
            </button>
            <button
              v-else
              @click="handleStepChange(1)"
              type="button"
              class="bg-transparent p-0 c-blue-400 text-sm fw-strong hover:c-blue-700"
            >
              {{ 'Change' | t }}
            </button>
          </li>
          <li v-if="step > 2">
            <p class="flex items-start justify-start flex-wrap gap-sm m-0 c-gray-700">
              <span class="text-base fw-strong c-gray-900">{{ 'Shipping Address' | t }}</span>
              <span>{{ stepLabels[2] ?? '' }}</span>
            </p>
            <button
              @click="handleStepChange(2)"
              type="button"
              class="bg-transparent p-0 c-blue-400 text-sm fw-strong hover:c-blue-700"
            >
              {{ 'Change' | t }}
            </button>
          </li>
          <li v-if="step > 3">
            <p class="flex items-start justify-start flex-wrap gap-sm m-0">
              <span class="text-base fw-strong c-gray-900">{{ 'Shipping' | t }}</span>
              <span class="flex flex-col gap-sm c-green-400" v-html="stepLabels[3] ?? ''" />
            </p>
            <button
              @click="handleStepChange(3)"
              type="button"
              class="bg-transparent p-0 c-blue-400 text-sm fw-strong hover:c-blue-700"
            >
              {{ 'Change' | t }}
            </button>
          </li>
          <li v-if="step > 4">
            <div class="steps-content--payment-method-grid">
              <p class="text-base fw-strong m-0">{{ 'Payment' | t }}</p>
              <p class="c-gray-700 m-0 text-capitalize">{{ stepLabels[4] ?? '' }}</p>
              <p class="text-base fw-strong m-0">{{ 'Billing' | t }}</p>
              <p class="c-gray-700 m-0">{{ stepLabels[5] ?? '' }}</p>
            </div>
            <button
              @click="handleStepChange(4)"
              type="button"
              class="bg-transparent p-0 c-blue-400 text-sm fw-strong hover:c-blue-700"
            >
              {{ 'Change' | t }}
            </button>
          </li>
        </ul>
        <div
          :class="{
            'marketplace-checkout--steps-status': true
          }"
        >
          <SloLoader
            :loading="loading || placeOrderLoading"
            :message="placeOrderLoading ? 'Your order is processing' : ''"
            size="xl"
          >
            <CheckoutStep1 v-if="step === 1" @continue="handleContinue" />
            <CheckoutStep2
              v-if="step === 2"
              :step-change="stepChange"
              @continue="handleContinue"
              @restrictedMessagesModalToggle="handleRestrictedMessagesModalToggle"
            />
            <CheckoutStep3
              v-if="step === 3"
              :step-change="stepChange"
              @continue="handleContinue"
              @restrictedMessagesModalToggle="handleRestrictedMessagesModalToggle"
            />
            <CheckoutStep4
              v-if="step === 4"
              :step-change="stepChange"
              :po-number="poNumber"
              @continue="handleContinue"
              @restrictedMessagesModalToggle="handleRestrictedMessagesModalToggle"
              @poNumberUpdate="handlePoNumberUpdate"
             />
            <CheckoutStep5
              v-if="step === 5"
              @continue="handlePlaceOrder"
              @restrictedMessagesModalToggle="handleRestrictedMessagesModalToggle"
            />
            <CheckoutConfirmation v-if="step === 6" :order-response="orderResponse" />
          </SloLoader>
        </div>
      </div>
      <div class="marketplace-checkout--order-summary-wrapper">
        <div class="marketplace-checkout--table">
          <header class="marketplace-checkout--table-header flex items-center justify-between flex-wrap">
            <h2 class="text-xl fw-strong m-0">{{ 'Order Summary' | t }}</h2>
            <SloLink v-if="step !== 6" :to="{name: 'marketplace-cart'}" class="text-base fw-strong">
              {{ 'Edit Order' | t }}
            </SloLink>
          </header>
          <div class="marketplace-checkout--table-body">
            <SloLoader key="cart-products-loader" :loading="cartLoading" size="xl">
              <CartProduct
                v-for="entry in orderResponse?.orderEntries || cartEntries"
                :key="`cart-entry-${entry?.entryNumber}`"
                :include-remove-button="false"
                :is-checkout="true"
                :entry-number="entry?.entryNumber"
                :quantity="entry?.quantity"
                :estimated-ship-date="entry?.estimatedShipDate"
                :price-each="entry?.product?.price?.formattedValue"
                :price-each-subscription="entry?.product?.subscriptionPrice?.formattedValue"
                :uom="entry?.product?.uom"
                :total-price="entry?.totalPrice?.formattedValue"
                :title="entry?.product?.number"
                :description="entry?.product?.title"
                :sku="entry?.product?.code"
                :product-type="entry?.product?.type"
                :subscription-frequencies="entry?.product?.subscriptionFrequencies"
                :subscription-frequency="entry?.subscriptionFrequency"
              >
                <template v-slot:image>
                  <SloProductImage :source="entry?.product?.images | getProductThumbnail" />
                </template>
              </CartProduct>
              <div class="marketplace-checkout--table-body padding-inline">
                <div class="marketplace-checkout--order-summary">
                  <div class="marketplace-checkout--order-summary--details">
                    <div class="marketplace-checkout--order-summary--details--item">
                      <p class="m-0">{{ 'Subtotal' | t }}</p>
                      <p class="m-0">{{ cartSubTotal }}</p>
                    </div>
                    <div class="marketplace-checkout--order-summary--details--item">
                      <p class="m-0">
                        {{ 'Coupon' | t }}
                        <span v-if="cartCouponCode">({{ cartCouponCode }})</span>
                        <span v-else>{{ 'Code' | t }}</span>
                      </p>

                      <p class="m-0">{{ cartCouponCode && cartDiscounts !== '$0.00' ? `-${cartDiscounts}` : '––' }}</p>
                    </div>
                    <div class="marketplace-checkout--order-summary--details--item">
                      <p class="m-0">{{ 'Shipping' | t }}</p>
                      <p class="m-0" v-html="cartShippingTotalFormatted" />
                    </div>
                    <div class="marketplace-checkout--order-summary--details--item">
                      <p class="m-0">{{ 'Taxes' | t }}</p>
                      <p class="m-0">{{ step > 3 ? cartTotalTax : '––' }}</p>
                    </div>
                  </div>
                  <div class="marketplace-checkout--order-summary--total">
                    <p class="m-0 fw-bold">{{ 'Total' | t }}</p>
                    <p class="m-0 text-xl">{{ step > 3 ? cartTotalPriceWithTax : cartTotal }}</p>
                  </div>
                </div>
              </div>
            </SloLoader>
          </div>
        </div>
      </div>
    </section>
    <section class="container">
      <MarketplaceFooter />
    </section>
    <transition name="fade-up">
      <SloModal
        v-if="showRestrictedMessagesModal"
        :max-width="680"
        @dismiss="showRestrictedMessagesModal = false"
        class="restrictions-modal"
      >
        <div>
          <h3 class="text-xl">{{ 'The following items in your cart require your attention.' | t }}</h3>
          <p class="mt-sm mb-0">{{ 'Please see below to update the items in your cart.' | t }}</p>
        </div>
        <div class="border-t-1 border-gray-400 mt-lg">
          <SloLoader key="restricted-products-loader" size="xl" :loading="cartLoading">
            <CartProduct
              v-for="entry in cartEntries.filter(entry => entry.product?.restrictedMessage.length)"
              :key="`cart-entry-${entry?.entryNumber}`"
              :include-remove-button="false"
              :is-checkout="true"
              :entry-number="entry?.entryNumber"
              :quantity="entry?.quantity"
              :price-each="entry?.product?.price?.formattedValue"
              :uom="entry?.product?.uom"
              :total-price="entry?.totalPrice?.formattedValue"
              :title="entry?.product?.number"
              :description="entry?.product?.title"
              :sku="entry?.product?.code"
              :product-type="entry?.product?.type"
              :restricted-message="entry?.product?.restrictedMessage"
              :should-display-product-actions="true"
              @removeProduct="handleRemoveProduct(entry?.entryNumber)"
            >
              <template v-slot:image>
                <SloProductImage :source="entry?.product?.images | getProductThumbnail" />
              </template>
            </CartProduct>
          </SloLoader>
        </div>
      </SloModal>
    </transition>
  </div>
</template>

<script>
import {mapState, mapActions, mapGetters} from 'vuex';
import t from '../../main/i18n';
import {getProductThumbnail, getCardNumberLastFourDigits, formatSingleLineAddress} from '../../main/utils';
import CartProduct from '../components/CartProduct.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import MarketplaceFooter from '../components/MarketplaceFooter.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import CheckoutStep1 from '../components/CheckoutStep1.vue';
import CheckoutStep2 from '../components/CheckoutStep2.vue';
import CheckoutStep3 from '../components/CheckoutStep3.vue';
import CheckoutStep4 from '../components/CheckoutStep4.vue';
import CheckoutStep5 from '../components/CheckoutStep5.vue';
import CheckoutConfirmation from '../components/CheckoutConfirmation.vue';
import SloanMarketPlaceLogo from '../../../img/Sloan-Marketplace-logo.svg';

export default {
  components: {
    CartProduct,
    SloLink,
    SloIcon,
    SloLoader,
    SloProductImage,
    MarketplaceFooter,
    SloLabel,
    SloField,
    SloInput,
    SloButton,
    SloModal,
    CheckoutStep1,
    CheckoutStep2,
    CheckoutStep3,
    CheckoutStep4,
    CheckoutStep5,
    CheckoutConfirmation,
    SloanMarketPlaceLogo
  },
  data() {
    return {
      loading: false,
      step: 1,
      stepChange: false,
      stepLabels: {2: null, 3: null, 4: null, 5: null},
      poNumber: '',
      placeOrderLoading: false,
      userLoginLoading: false,
      showRestrictedMessagesModal: false
    };
  },
  filters: {
    t,
    getProductThumbnail,
    getCardNumberLastFourDigits
  },
  async created() {
    this.loading = true;
    this.$store.commit('marketplace/update_order_response', null);

    if (this.step === 1 && this.cartResponse?.user?.name === 'guest') {
      this.step = 2;
    }

    await this.getCurrentCart({sync: true});

    if (!this.cartResponse) {
      await this.$router.push({name: 'marketplace-cart'});
    }

    if (this.authenticated) {
      this.step = 2;
    }

    this.loading = false;
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapGetters('marketplace', [
      'cartEntries',
      'cartHasRestrictedMessages',
      'cartDeliveryModes',
      'cartTotal',
      'cartSubTotal',
      'cartDiscounts',
      'cartCouponCode',
      'cartShippingTotal',
      'cartTotalTax',
      'cartTotalPriceWithTax'
    ]),
    ...mapState('marketplace', ['cartLoading', 'cartResponse', 'orderResponse']),
    shouldRedirectToCart() {
      return !this.userLoginLoading && !this.cartEntries?.length && this.step !== 6;
    },
    cartShippingTotalFormatted() {
      const cartShippingTotal = this.cartShippingTotal;

      if (this.step > 3 && cartShippingTotal === '$0.00') return t('FREE');
      if (this.step < 4 || cartShippingTotal === '$0.00') return '––';

      return cartShippingTotal;
    }
  },
  watch: {
    async authenticated(newValue) {
      if (newValue) {
        this.userLoginLoading = true;
        await this.getCurrentCart({sync: true, mergeCart: true});
      }
      this.step = newValue ? 2 : 1;
      this.userLoginLoading = false;
    },
    async cartEntries() {
      if (this.shouldRedirectToCart) {
        this.$snotify.info(
          t('Please add items to your cart before checking out'),
          {timeout: 6000}
        );
        await this.$router.push({name: 'marketplace-cart'});
      }
    },
    cartResponse(newValue) {
      // If product(s) have been removed on ECC sync call, show message from API
      if (newValue?.removedProducts) {
        this.$snotify.error(newValue.removedProducts);
      }

      // Build Step labels
      if (newValue?.deliveryAddress) {
        this.stepLabels[2] = formatSingleLineAddress(newValue?.deliveryAddress);
      }
      if (this.cartDeliveryModes) {
        this.stepLabels[3] = this.cartDeliveryModes;
      }
      if (newValue?.paymentInfo) {
        this.stepLabels[4] =
          `${newValue.paymentInfo?.cardType?.code} •••• ${getCardNumberLastFourDigits(newValue.paymentInfo?.cardNumber)},
          ${newValue.paymentInfo?.accountHolderName},
          ${t('Expires')} ${newValue.paymentInfo?.expiryMonth}/${newValue.paymentInfo?.expiryYear}`;
      }
      if (newValue?.paymentInfo?.billingAddress) {
        this.stepLabels[5] = formatSingleLineAddress(newValue?.paymentInfo?.billingAddress);
      }
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('marketplace', ['getCurrentCart', 'removeCartEntry', 'placeOrder']),
    async handleStepChange(step) {
      await this.getCurrentCart()
        .then(() => {
          this.step = step;
          this.stepChange = true;
        })
        .catch(() => this.step = 1);

      window.scrollTo(0, 0);
    },
    handleRestrictedMessagesModalToggle(value) {
      this.showRestrictedMessagesModal = value;
    },
    async handleContinue(response) {
      this.stepChange = false;

      if (response === 'back') {
        window.scrollTo(0, 0);
        this.step = this.step - 1;
        window.scrollTo(0, 0);
        return;
      }

      await this.getCurrentCart()
        .then(() => this.step = this.step + 1)
        .catch(() => this.step = this.step - 1);

      window.scrollTo(0, 0);
    },
    async handleRemoveProduct(entryNumber) {
      await this.removeCartEntry({entryNumber});
      this.showRestrictedMessagesModal = this.cartHasRestrictedMessages;
    },
    async handlePlaceOrder() {
      this.step = 6;
      this.placeOrderLoading = true;
      await this.placeOrder(this.poNumber)
        .then(() => {
          this.placeOrderLoading = false;
          window.scrollTo(0, 0);
        })
        .catch(() => {
          this.step = 5;
          this.placeOrderLoading = false;
          this.$snotify.error(t('Error placing your order'));
          window.scrollTo(0, 0);
        });
    },
    handlePoNumberUpdate(value) {
      this.poNumber = value;
    },
    handleLogout() {
      this.logout('logout_request');
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-checkout {
  height: 100vh;

  &--table {
    border: 1px solid $color-gray-400;
    border-radius: 0.8rem;

    &-header {
      padding: 1.6rem;

      @media (min-width: $screen-md) {
        padding: 2.4rem;
      }
    }

    &-body {
      &.padding-full {
        padding: 1.6rem;

        @media (min-width: $screen-md) {
          padding: 2.4rem;
        }
      }
      &.padding-inline {
        padding-inline: 1.6rem;

        @media (min-width: $screen-md) {
          padding-inline: 2.4rem
        }
      }
      &.padding-block {
        padding-block: 1.6rem;

        @media (min-width: $screen-md) {
          padding-block: 2.4rem
        }
      }
    }

    & > * + * {
      border-top: 1px solid $color-gray-400;
    }

    .slo-cart-product {
      padding-inline: 1.6rem;

      @media (min-width: $screen-md) {
        padding-inline: 2.4rem
      }
    }
  }
}
</style>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-checkout {
  &--nav {
    position: sticky;
    top: 0;
    width: 100%;
    background-color: $color-white;
    z-index: 100;
    padding-block: 2.4rem;
    border-bottom: 1px solid $color-gray-400;

    .marketplace-logo {
      width: 100%;
      height: auto;
      max-width: 312px;
    }
  }

  &--nav-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 2.4rem;
  }

  &--nav-link {
    font-size: 1.4rem;
    font-weight: 500;
  }

  &--wrapper {
    display: grid;
    gap: 3.2rem;
    width: 100%;
    margin-block-start: 3.2rem;

    @media (min-width: $screen-md) {
      grid-template-columns: 2fr 1fr;
    }
  }

  &--steps {
    display: flex;
    align-items: center;
    justify-content: start;
    flex-wrap: wrap;
    gap: 0.8rem;
    padding: 0;
    list-style: none;
    margin-block-start: 3.2rem;

    li {
      @include text('sm');
      display: flex;
      align-items: center;
      justify-content: start;
      gap: 0.8rem;
      color: $color-gray-500;

      span {
        color: $color-gray-500;
        font-weight: 500;
        text-transform: uppercase;
      }

      &.active {
        span {
          color: $color-blue-400;
        }
      }
    }
  }

  &--form-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2.4rem;
    width: 100%;
    margin-block-start: 2.4rem;
  }

  &--form-name {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: start;
    gap: 1.6rem;

    @media (min-width: $screen-sm) {
      flex-direction: row;
    }

    & > .slo-field {
      flex: 1;
    }
  }

  &--form-button-wrapper {
    display: flex;
    align-items: stretch;
    justify-items: start;
    flex-wrap: wrap;
    gap: 1.6rem;

    & > .slo-button {
      flex: 1;
    }
  }

  &--steps-content-preview {
    list-style: none;
    padding: 0;
    margin-bottom: 3.2rem;
    border: 1px solid $color-gray-400;
    border-radius: 0.8rem;

    li {
      display: flex;
      align-items: start;
      justify-content: space-between;
      gap: 0.8rem;
      padding: 1.6rem;

      &:not(:first-child) {
        border-top: 1px solid $color-gray-400;
      }
    }

    .steps-content--payment-method-grid {
      display: grid;
      row-gap: 0.8rem;

      @media (min-width: $screen-sm) {
        grid-template-columns: auto 1fr;
        column-gap: 0.8rem;
        row-gap: 1.6rem;
      }
    }

    p span {
      word-break: break-word;
    }
  }

  .slo-link:after {
    display: none;
  }

  .error {
    @include text('sm', 'strong');
    color: $color-red-400;
  }

  &--agree {
    :deep(.slo-field) {
      flex-direction: row;
      align-items: baseline;
      gap: 8px;

      input:checked {
        background-color: $color-blue-400;

        &:before {
          display: flex;
          align-items: center;
          justify-content: center;
          background-size: contain;
          top: 50%;
          left: 50%;
          margin: 0;
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="white" width="18px" height="18px"><path d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" fill="white" /></svg>') !important;
        }
      }
    }
  }

  &--order-summary {
    margin-block-start: 3.2rem;

    @media (min-width: $screen-md) {
      margin-block-start: 0;
    }

    &-wrapper {
      :deep(.slo-loader-icon) {
        min-height: 195px;
      }
    }

    &--details {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      padding-block: 2.4rem;
      border-bottom: 1px solid #d1d5db;

      &--item {
        display: flex;
        justify-content: space-between;
      }
    }

    &--total {
      display: flex;
      justify-content: space-between;
      margin-block-start: 1.6rem;
      padding-block: 1.6rem;
    }

    &--button-wrapper {
      display: flex;
      align-items: stretch;
      justify-content: start;
      gap: 1.6rem;
      flex-wrap: wrap;
      margin-block-start: 1.6rem;

      > * {
        flex: 1 1 48%;
      }
    }
  }

  .container {
    &:before, &:after {
      display: none;
    }
  }

  :deep(.slo-cart-product:not(.up-sell)) {

    .slo-cart-product--wrapper {
      grid-template-columns: -moz-fit-content(90px) 1fr 1fr 1fr;
      grid-template-columns: fit-content(90px) 1fr 1fr 1fr;
      padding-inline: 0;
    }

    .slo-cart-product--description {
      max-width: none;
    }
    .slo-cart-product--price {
      grid-column: 2 / 5;
      justify-content: space-between;
      width: 100%;

      .slo-price-info {
        flex: 0;

        span {
          @media (min-width: $screen-md) {
            text-align: right;
          }
        }
      }
    }

    .slo-product-image img {
      width: 100%;
      height: 100%;
    }
  }
}

:deep(.slo-modal-root).restrictions-modal {
  header {
    button {
      padding: 0 !important;
    }
  }

  .slo-cart-product--wrapper {
    @media (min-width: $screen-md) {
      padding-inline: 2.4rem !important;
    }
  }
}
</style>
